﻿package org.example.project.engineslot

import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.example.project.enginekartei.KarteiApi
import org.example.project.enginekalender.kalenderAddDays
import org.example.project.enginekalender.kalenderTodayIsoDate
import org.example.project.enginekalender.kalenderWeekdayOf
import org.example.project.enginekalender.kalenderWeekdayShortName
import org.example.project.enginekalender.parseKalenderIsoDate
import org.example.project.service.ZustandManager
import org.example.project.utils.All

const val SLOT_VIEW_OPERATIV = "OPERATIV"
const val SLOT_VIEW_SETUP = "SETUP"

const val SLOT_TYP = "SLOT"
const val SLOT_BEREICH_WE = "WE"
const val SLOT_BEREICH_WA = "WA"
const val SLOT_MODUS_SINGLE = "single"
const val SLOT_MODUS_MULTI = "multi"

const val SLOT_MASTER_EMPFAENGER = "Empfänger"
const val SLOT_MASTER_AUFTRAGGEBER = "Auftraggeber"
const val SLOT_MASTER_FAHRER = "Fahrer"
const val SLOT_MASTER_LADEFIRMA = "Ladefirma"

const val SLOT_EXTRA_INTERVAL = "slotIntervallMinuten"
const val SLOT_EXTRA_COUNT = "slotAnzahl"
const val SLOT_EXTRA_COUNT_WE = "slotAnzahlWe"
const val SLOT_EXTRA_COUNT_WA = "slotAnzahlWa"
const val SLOT_EXTRA_INDEX = "slotIndex"
const val SLOT_EXTRA_BEREICH = "slotBereich"
const val SLOT_EXTRA_DISABLED_CELLS = "slotDeaktivierteFenster"
const val SLOT_EXTRA_ABHOLORT = "slotAbholort"
const val SLOT_EXTRA_BOOKING_DATE = "slotDatum"
const val SLOT_EXTRA_STANDORT_ID = "slotStandortId"
const val SLOT_EXTRA_BOOKING_LEAD_HOURS = "slotBuchungVorlaufStunden"
const val SLOT_EXTRA_BOOKING_LEAD_DAYS = "slotBuchungVorlaufTage"
const val SLOT_KALENDER_TERMINART = "Slotbuchung"
const val SLOT_ART = "SLOT"

private val slotPayloadJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = true
}

fun slotDefaultDate(): String {
    return kalenderTodayIsoDate()
}

fun slotDefaultConfig(): All =
    All(
        id = "slot-config",
        bezeichnung = "Slot Konfiguration",
        uhrzeitVon = "06:00",
        uhrzeitBis = "18:00",
        slotIntervallMinuten = 30,
        slotAnzahl = 6,
        slotBuchungVorlaufStunden = 0,
        slotBuchungVorlaufTage = 365
    )

fun slotIntervalMinutes(config: All): Int =
    config.slotIntervallMinuten.coerceIn(15, 180)

private fun slotLegacyWeCount(config: All): Int =
    config.slotAnzahlWe.coerceIn(1, 20)

private fun slotLegacyWaCount(config: All): Int =
    config.slotAnzahlWa.coerceIn(1, 20)

fun slotCount(config: All): Int =
    config.slotAnzahl.takeIf { it > 0 }?.coerceIn(1, 40)
        ?: (slotLegacyWeCount(config) + slotLegacyWaCount(config)).coerceIn(1, 40)

fun slotBookingLeadHours(config: All): Int =
    config.slotBuchungVorlaufStunden.coerceIn(0, 720)

fun slotBookingLeadDays(config: All): Int =
    config.slotBuchungVorlaufTage.coerceIn(0, 365)

fun slotConfigUpdated(
    config: All,
    intervalMinutes: Int,
    von: String,
    bis: String,
    slotCount: Int,
    bookingLeadHours: Int = slotBookingLeadHours(config),
    bookingLeadDays: Int = slotBookingLeadDays(config)
): All =
    config.copy(
        uhrzeitVon = von,
        uhrzeitBis = bis,
        slotIntervallMinuten = intervalMinutes,
        slotAnzahl = slotCount,
        slotBuchungVorlaufStunden = bookingLeadHours,
        slotBuchungVorlaufTage = bookingLeadDays
    )

fun slotTimeWindows(config: All): List<All> {
    val from = parseTimeMinutes(config.uhrzeitVon) ?: return emptyList()
    val to = parseTimeMinutes(config.uhrzeitBis) ?: return emptyList()
    val interval = slotIntervalMinutes(config)
    if (from >= to || interval <= 0) return emptyList()
    return buildList {
        var start = from
        while (start + interval <= to) {
            val end = start + interval
            val fromText = formatTimeMinutes(start)
            val toText = formatTimeMinutes(end)
            add(
                All(
                    id = "${fromText}_${toText}",
                    uhrzeitVon = fromText,
                    uhrzeitBis = toText,
                    label = "$fromText - $toText"
                )
            )
            start += interval
        }
    }
}

fun slotMasterTypes(): List<String> = listOf(
    SLOT_MASTER_EMPFAENGER,
    SLOT_MASTER_AUFTRAGGEBER,
    SLOT_MASTER_FAHRER,
    SLOT_MASTER_LADEFIRMA
)

fun slotLabelForMaster(entry: All): String =
    buildList {
        add(entry.firma.ifBlank { entry.name })
        add(entry.ort)
        add(entry.strasse)
    }.filter { it.isNotBlank() }.joinToString(" · ").ifBlank { entry.id }

fun slotDropdownOptions(entries: List<All>, typ: String): List<All> =
    listOf(All(key = "", label = "Keine Auswahl")) +
        entries
            .filter {
                it.id.isNotBlank() || it.firmenId.isNotBlank() || it.firma.isNotBlank() || it.name.isNotBlank()
            }
            .sortedBy { slotLabelForMaster(it).lowercase() }
            .map { entry ->
                All(
                    key = slotLabelForMaster(entry),
                    label = slotLabelForMaster(entry)
                )
            }

fun slotMasterEntries(entries: List<All>, typ: String): List<All> =
    entries.filter {
        it.id.isNotBlank() || it.firmenId.isNotBlank() || it.firma.isNotBlank() || it.name.isNotBlank()
    }.sortedBy { slotLabelForMaster(it).lowercase() }

fun slotMasterEntryBySelection(entries: List<All>, typ: String, selectedValue: String): All? =
    slotMasterEntries(entries, typ).firstOrNull { slotLabelForMaster(it) == selectedValue }

fun slotBookingForCell(
    bookings: List<All>,
    config: All,
    datum: String,
    slotIndex: Int,
    timeWindow: All,
    standort: String = ""
): All? =
    bookings.firstOrNull {
        it.datum == datum &&
            slotBookingIndex(it, config) == slotIndex &&
            it.uhrzeitVon == timeWindow.uhrzeitVon &&
            (standort.isBlank() || it.standort == standort || it.standort.isBlank())
    }

fun slotBookingDraft(
    datum: String,
    slotIndex: Int,
    timeWindow: All,
    standort: String = "",
    booking: All? = null
): All =
    (booking ?: All()).copy(
        id = booking?.id.orEmpty(),
        datum = datum,
        slotDatum = datum,
        typ = SLOT_TYP,
        uhrzeitVon = timeWindow.uhrzeitVon,
        uhrzeitBis = timeWindow.uhrzeitBis,
        zustand = slotNormalizedState(
            booking?.zustand ?: 0,
            booking = booking,
            disabled = false
        ),
        standort = standort.ifBlank { booking?.standort.orEmpty() },
        slotBereich = booking?.slotBereich.orEmpty(),
        slotIndex = slotIndex.toString(),
        slotAbholort = booking?.slotAbholort.orEmpty(),
        slotStandortId = booking?.slotStandortId.orEmpty()
    )

fun slotCellSelection(
    datum: String,
    slotIndex: Int,
    timeWindow: All,
    standort: String,
    booking: All? = null
): All =
    slotBookingDraft(
        datum = datum,
        slotIndex = slotIndex,
        timeWindow = timeWindow,
        standort = standort,
        booking = booking
    ).copy(
        standort = standort.ifBlank { booking?.standort.orEmpty() }
    )

fun slotSameCell(left: All?, right: All?): Boolean {
    if (left == null || right == null) return false
    val leftIndex = left.slotIndex
    val rightIndex = right.slotIndex
    val sameIndex = when {
        leftIndex.isNotBlank() && rightIndex.isNotBlank() -> leftIndex == rightIndex
        left.id.isNotBlank() && right.id.isNotBlank() -> left.id == right.id
        else -> false
    }
    val sameStandort =
        left.standort == right.standort || left.standort.isBlank() || right.standort.isBlank()
    return sameIndex &&
        left.datum == right.datum &&
        left.uhrzeitVon == right.uhrzeitVon &&
        sameStandort
}

fun slotCellKey(slotIndex: Int, timeWindow: All): String =
    listOf(slotIndex.toString(), timeWindow.uhrzeitVon, timeWindow.uhrzeitBis)
        .joinToString("|")

fun slotDisabledCellKeys(config: All): Set<String> =
    config.slotDeaktivierteFenster
        .split(",")
        .map { it.trim() }
        .filter { it.isNotBlank() }
        .toSet()

fun slotIsCellDisabled(config: All, slotIndex: Int, timeWindow: All): Boolean =
    slotCellKey(slotIndex, timeWindow) in slotDisabledCellKeys(config)

fun slotToggleDisabledCell(config: All, slotIndex: Int, timeWindow: All): All {
    val current = slotDisabledCellKeys(config).toMutableSet()
    val key = slotCellKey(slotIndex, timeWindow)
    if (!current.add(key)) current.remove(key)
    return config.copy(
        slotDeaktivierteFenster = current.sorted().joinToString(",")
    )
}

fun slotNormalizedState(code: Int, booking: All? = null, disabled: Boolean = false): Int {
    if (code in ZustandManager.Slot.allCodes()) return code
    if (disabled) return ZustandManager.Slot.GESPERRT
    if (code == ZustandManager.RESERVIERT) return ZustandManager.Slot.RESERVIERT
    if (slotHasBookingData(booking)) return ZustandManager.Slot.BELEGT
    return ZustandManager.Slot.FREI
}

fun slotStatusLabel(code: Int, booking: All? = null, disabled: Boolean = false): String =
    ZustandManager.Slot.textFor(slotNormalizedState(code, booking, disabled))

fun slotCellState(booking: All?, disabled: Boolean = false): Int =
    slotNormalizedState(booking?.zustand ?: 0, booking, disabled)

fun slotAbholortOptions(standorte: List<All>): List<All> =
    standorte
        .filter { it.id.isNotBlank() }
        .distinctBy { it.id }
        .sortedBy {
            it.standort
                .ifBlank { it.id }
                .lowercase()
        }
        .map { standort ->
            All(
                key = standort.id,
                label = standort.standort
                    .ifBlank { standort.ort }
                    .ifBlank { standort.id }
            )
        }

fun slotSelectedAbholort(booking: All, standorte: List<All>): String {
    val stored = booking.slotAbholort
    if (stored.isNotBlank()) {
        val byId = slotAbholortOptions(standorte).firstOrNull { it.key == stored }?.key
        if (!byId.isNullOrBlank()) return byId
        val byLabel = slotAbholortOptions(standorte).firstOrNull { it.label == stored }?.key
        if (!byLabel.isNullOrBlank()) return byLabel
    }
    return booking.standort.ifBlank { standorte.firstOrNull()?.id.orEmpty() }
}

fun slotAbholortLabel(abholortKey: String, standorte: List<All>): String =
    slotAbholortOptions(standorte).firstOrNull { it.key == abholortKey }?.label
        ?: standorte.firstOrNull { it.id == abholortKey }?.standort
            .orEmpty()

fun slotStatusOptions(currentCode: Int): List<All> =
    slotAllStatusCodes().map { code ->
        All(
            key = code.toString(),
            label = slotStatusLabel(code),
            zustand = code
        )
    }

fun slotAllStatusCodes(): List<Int> = listOf(
    ZustandManager.Slot.FREI,
    ZustandManager.Slot.IN_BUCHUNG,
    ZustandManager.Slot.RESERVIERT,
    ZustandManager.Slot.GESPERRT,
    ZustandManager.Slot.BELEGT
)

fun slotNextStatusCodes(currentCode: Int): List<Int> = when (slotNormalizedState(currentCode)) {
    ZustandManager.Slot.FREI -> listOf(ZustandManager.Slot.IN_BUCHUNG, ZustandManager.Slot.GESPERRT)
    ZustandManager.Slot.IN_BUCHUNG -> listOf(ZustandManager.Slot.RESERVIERT, ZustandManager.Slot.BELEGT, ZustandManager.Slot.FREI)
    ZustandManager.Slot.RESERVIERT -> listOf(ZustandManager.Slot.BELEGT, ZustandManager.Slot.FREI)
    ZustandManager.Slot.GESPERRT -> listOf(ZustandManager.Slot.FREI)
    ZustandManager.Slot.BELEGT -> listOf(ZustandManager.Slot.RESERVIERT, ZustandManager.Slot.FREI)
    else -> emptyList()
}

fun slotStatusColor(code: Int): String =
    ZustandManager.Slot.colorHexFor(slotNormalizedState(code))

fun slotStatusBadgeText(booking: All?): String =
    slotStatusLabel(booking?.zustand ?: ZustandManager.Slot.FREI, booking)

fun slotBookingArtCode(booking: All): String {
    val explicit = booking.auftragsart.trim().uppercase()
    if (explicit == SLOT_BEREICH_WA || explicit == SLOT_BEREICH_WE) return explicit

    return ""
}

fun slotBookingFlowMode(booking: All): String =
    when (slotBookingArtCode(booking)) {
        SLOT_BEREICH_WE -> if (slotLiefernBelege(booking).count { it.lieferscheinnummer.isNotBlank() } > 1) {
            SLOT_MODUS_MULTI
        } else {
            SLOT_MODUS_SINGLE
        }
        SLOT_BEREICH_WA -> if (slotLadenTouren(booking).count { it.lieferscheinnummer.isNotBlank() } > 1) {
            SLOT_MODUS_MULTI
        } else {
            SLOT_MODUS_SINGLE
        }
        else -> SLOT_MODUS_SINGLE
    }

fun slotHasBookingData(booking: All?): Boolean {
    if (booking == null) return false

    val art = slotBookingArtCode(booking)
    val artHasPrimaryReference = when (art) {
        SLOT_BEREICH_WE -> booking.lieferscheinnummer.isNotBlank()
        SLOT_BEREICH_WA -> booking.lieferscheinnummer.isNotBlank()
        else -> booking.lieferscheinnummer.isNotBlank() || booking.lieferscheinnummer.isNotBlank() || booking.lieferscheinnummer.isNotBlank()
    }

    return artHasPrimaryReference ||
        booking.empfaenger.isNotBlank() ||
        booking.auftraggeber.isNotBlank() ||
        booking.unternehmer.isNotBlank() ||
        booking.lkwkennzeichen.isNotBlank() ||
        booking.palettenanzahl > 0
}

fun slotIsBooked(booking: All?): Boolean =
    booking != null && slotCellState(booking) in listOf(
        ZustandManager.Slot.IN_BUCHUNG,
        ZustandManager.Slot.RESERVIERT,
        ZustandManager.Slot.BELEGT
    )

fun slotUseReadonlyDoubleClick(booking: All?): Boolean =
    booking != null &&
        slotIsBooked(booking) &&
        slotBookingArtCode(booking) == SLOT_BEREICH_WE

fun slotIsClickable(booking: All?, disabled: Boolean): Boolean {
    if (disabled) return false
    return slotCellState(booking, disabled) != ZustandManager.Slot.IN_BUCHUNG
}

fun slotBookingStateForSave(booking: All): Int =
    if (slotHasBookingData(booking)) ZustandManager.Slot.BELEGT else ZustandManager.Slot.RESERVIERT

fun slotCellRecordForState(
    datum: String,
    slotIndex: Int,
    timeWindow: All,
    standort: String,
    existing: All?,
    disabled: Boolean,
    activeBookingId: String = ""
): All {
    val base = slotCellSelection(
        datum = datum,
        slotIndex = slotIndex,
        timeWindow = timeWindow,
        standort = standort,
        booking = existing
    )
    val nextState = when {
        existing != null && slotHasBookingData(existing) -> slotNormalizedState(existing.zustand, existing, disabled)
        existing != null &&
            existing.id == activeBookingId &&
            slotNormalizedState(existing.zustand, existing, disabled) == ZustandManager.Slot.IN_BUCHUNG ->
            ZustandManager.Slot.IN_BUCHUNG
        existing != null && slotNormalizedState(existing.zustand, existing, disabled) == ZustandManager.Slot.RESERVIERT ->
            ZustandManager.Slot.RESERVIERT
        disabled -> ZustandManager.Slot.GESPERRT
        else -> ZustandManager.Slot.FREI
    }
    return base.copy(zustand = nextState)
}

fun slotEntriesForDate(
    config: All,
    datum: String,
    standort: String,
    existingBookings: List<All>,
    activeBookingId: String = ""
): List<All> =
    slotUnifiedRows(config).flatMap { row ->
        slotTimeWindows(config).map { timeWindow ->
            val existing = slotBookingForCell(
                bookings = existingBookings,
                config = config,
                datum = datum,
                slotIndex = row.lfdNr,
                timeWindow = timeWindow,
                standort = standort
            )
            slotCellRecordForState(
                datum = datum,
                slotIndex = row.lfdNr,
                timeWindow = timeWindow,
                standort = standort,
                existing = existing,
                disabled = slotIsCellDisabled(config, row.lfdNr, timeWindow),
                activeBookingId = activeBookingId
            )
        }
    }

fun slotUpsert(bookings: List<All>, booking: All): List<All> =
    bookings.filterNot { it.id == booking.id } + booking

fun slotDelete(bookings: List<All>, bookingId: String): List<All> =
    bookings.filterNot { it.id == bookingId }

private fun slotStandortLabel(entry: All): String =
    listOf(
        listOf(entry.strasse, entry.hausnummer).filter { it.isNotBlank() }.joinToString(" "),
        listOf(entry.plz, entry.ort).filter { it.isNotBlank() }.joinToString(" ")
    ).filter { it.isNotBlank() }.joinToString(", ")
        .ifBlank { entry.standort.ifBlank { entry.id } }

fun slotStandortOptions(standorte: List<All>): List<All> =
    standorte
        .filter { it.id.isNotBlank() }
        .distinctBy { it.id }
        .sortedBy { it.id.toIntOrNull() ?: Int.MAX_VALUE }
        .map {
            All(
                key = it.id,
                label = slotStandortLabel(it)
            )
        }

fun slotSelectedStandort(config: All, standorte: List<All>): String =
    config.standort.ifBlank { slotStandortOptions(standorte).firstOrNull()?.key.orEmpty() }

fun slotStandortEntry(standorte: List<All>, selectedStandort: String): All? =
    standorte.firstOrNull { it.id == selectedStandort }

fun slotStandortHeadline(standorte: List<All>, selectedStandort: String): String =
    slotStandortEntry(standorte, selectedStandort)?.firma
        ?.ifBlank { selectedStandort }
        .orEmpty()

fun slotStandortAddress(standorte: List<All>, selectedStandort: String): String =
    slotStandortEntry(standorte, selectedStandort)?.let(::slotStandortLabel).orEmpty()

fun slotDisplayDate(isoDate: String): String {
    val parsed = parseKalenderIsoDate(isoDate) ?: return isoDate
    val year = parsed.year.takeIf { it > 0 } ?: return isoDate
    val month = parsed.month.takeIf { it > 0 } ?: return isoDate
    val day = parsed.day.takeIf { it > 0 } ?: return isoDate
    val weekday = kalenderWeekdayShortName(kalenderWeekdayOf(year, month, day))
    return "$weekday ${day.toString().padStart(2, '0')}.${month.toString().padStart(2, '0')}.${year.toString().padStart(4, '0')}"
}

fun slotShiftDate(isoDate: String, deltaDays: Int): String {
    val parsed = parseKalenderIsoDate(isoDate) ?: return isoDate
    val year = parsed.year.takeIf { it > 0 } ?: return isoDate
    val month = parsed.month.takeIf { it > 0 } ?: return isoDate
    val day = parsed.day.takeIf { it > 0 } ?: return isoDate
    return kalenderAddDays(year, month, day, deltaDays)
}

fun slotBookingTitle(booking: All): String =
    when (slotBookingArtCode(booking)) {
        SLOT_BEREICH_WE -> booking.lieferscheinnummer
        SLOT_BEREICH_WA -> booking.lieferscheinnummer
        else -> booking.lieferscheinnummer.ifBlank { booking.lieferscheinnummer.ifBlank { booking.lieferscheinnummer } }
    }.ifBlank { "Slot ${booking.slotIndex}" }

fun slotBookingHeadline(booking: All): String =
    "Slot ${booking.slotIndex} | ${booking.uhrzeitVon} bis ${booking.uhrzeitBis}"

suspend fun loadSlotBookingMasterData(): List<All> {
    val mandanten = runCatching { KarteiApi.loadMandanten() }.getOrDefault(emptyList())
    val eigenesUnternehmenRoh = runCatching { KarteiApi.loadUnternehmen() }.getOrNull()
    val eigenesUnternehmen = if (
        eigenesUnternehmenRoh != null && (
            eigenesUnternehmenRoh.firma.isNotBlank() ||
                eigenesUnternehmenRoh.name.isNotBlank() ||
                eigenesUnternehmenRoh.firmenId.isNotBlank() ||
                eigenesUnternehmenRoh.id.isNotBlank()
            )
    ) {
        eigenesUnternehmenRoh.copy(
            id = eigenesUnternehmenRoh.id.ifBlank {
                eigenesUnternehmenRoh.firmenId.ifBlank { "unternehmenstamm" }
            },
            firmenId = eigenesUnternehmenRoh.firmenId.ifBlank { SlotSnapCompanyApi.currentFirmenId() },
            firma = eigenesUnternehmenRoh.firma.ifBlank { eigenesUnternehmenRoh.name }
        )
    } else {
        null
    }

    return (listOfNotNull(eigenesUnternehmen) + mandanten)
        .distinctBy { entry ->
            entry.firmenId.ifBlank {
                entry.id.ifBlank {
                    slotLabelForMaster(entry)
                }
            }
        }
}

fun slotTimeAxisLabel(time: String): String {
    val parts = time.split(":")
    if (parts.size != 2) return time
    val hour = parts[0].toIntOrNull() ?: return time
    val minute = parts[1].padStart(2, '0')
    return if (minute == "00") "${hour.toString().padStart(2, '0')}:00" else ""
}

fun slotUnifiedRows(config: All): List<All> =
    (1..slotCount(config)).map { index -> All(lfdNr = index) }

private fun slotNowMillis(): Double =
    js("Date.now()") as Double

private fun slotDateMillis(isoDate: String, time: String): Double {
    val parts = isoDate.split("-")
    if (parts.size != 3) return Double.NaN
    val year = parts[0].toIntOrNull() ?: return Double.NaN
    val month = parts[1].toIntOrNull() ?: return Double.NaN
    val day = parts[2].toIntOrNull() ?: return Double.NaN
    val minutes = parseTimeMinutes(time) ?: return Double.NaN
    val hour = minutes / 60
    val minute = minutes % 60
    return js("new Date(year, month - 1, day, hour, minute, 0, 0).getTime()") as Double
}

private fun slotWindowStartMillis(selectedDate: String, timeWindow: All): Double =
    slotDateMillis(selectedDate, timeWindow.uhrzeitVon)

private fun slotWindowEndMillis(selectedDate: String, timeWindow: All): Double =
    slotDateMillis(selectedDate, timeWindow.uhrzeitBis)

private fun slotBookableUntilMillis(config: All): Double =
    slotNowMillis() + slotBookingLeadDays(config).toDouble() * 24.0 * 60.0 * 60.0 * 1000.0

private fun slotBookableFromMillis(config: All): Double =
    slotNowMillis() + slotBookingLeadHours(config).toDouble() * 60.0 * 60.0 * 1000.0

fun slotIsSetupWindowVisible(config: All, selectedDate: String, timeWindow: All): Boolean {
    val startMillis = slotWindowStartMillis(selectedDate, timeWindow)
    val endMillis = slotWindowEndMillis(selectedDate, timeWindow)
    if (startMillis.isNaN() || endMillis.isNaN()) return false
    val now = slotNowMillis()
    if (endMillis <= now) return false
    if (startMillis < slotBookableFromMillis(config)) return false
    if (startMillis > slotBookableUntilMillis(config)) return false
    return true
}

fun slotIsSetupCellBookable(config: All, selectedDate: String, timeWindow: All): Boolean {
    val startMillis = slotWindowStartMillis(selectedDate, timeWindow)
    val endMillis = slotWindowEndMillis(selectedDate, timeWindow)
    if (startMillis.isNaN() || endMillis.isNaN()) return false
    val now = slotNowMillis()
    if (endMillis <= now) return false
    if (startMillis < slotBookableFromMillis(config)) return false
    if (startMillis > slotBookableUntilMillis(config)) return false
    return true
}

private fun slotLegacyBereich(booking: All): String =
    booking.auftragsart.ifBlank { booking.typ }

private fun slotBookingIndex(booking: All, config: All): Int {
    // The current slot grid uses one unified row index across all bookings.
    // Shifting WA entries by a legacy WE offset causes the persisted slot to
    // appear on a different row than the one the user selected.
    return booking.slotIndex.toIntOrNull() ?: return -1
}

fun parseTimeMinutes(value: String): Int? {
    val parts = value.split(":")
    if (parts.size != 2) return null
    val hour = parts[0].toIntOrNull() ?: return null
    val minute = parts[1].toIntOrNull() ?: return null
    if (hour !in 0..23 || minute !in 0..59) return null
    return hour * 60 + minute
}

fun formatTimeMinutes(total: Int): String {
    val hour = total / 60
    val minute = total % 60
    return "${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}"
}

